import type { I18nKey } from "../../i18n";
import { Fragment, memo, type ReactNode } from "react";
import { EllipsisIcon, ListTodoIcon } from "lucide-react";
import { Button } from "../ui/button";
import {
  Menu,
  MenuItem,
  MenuPopup,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator as MenuDivider,
  MenuTrigger,
} from "../ui/menu";
import { useTranslation } from "react-i18next";

export const CompactComposerControlsMenu = memo(function CompactComposerControlsMenu(props: {
  activePlan: boolean;
  planSidebarLabel: string;
  planSidebarOpen: boolean;
  showDeepWorkToggle: boolean;
  deepWorkEnabled: boolean;
  deepWorkDisabled?: boolean;
  deepWorkDisabledReason?: I18nKey | null;
  onToggleDeepWork: () => void;
  onTogglePlanSidebar: () => void;
}) {
  const { t } = useTranslation();
  const planSidebarLabel = props.planSidebarLabel.toLowerCase();
  const sections: Array<{ key: string; node: ReactNode }> = [];
  if (props.showDeepWorkToggle) {
    sections.push({
      key: "deep-work",
      node: (
        <>
          <div
            className="px-2 py-1.5 font-medium text-muted-foreground text-xs"
            title={props.deepWorkDisabledReason ? t(props.deepWorkDisabledReason) : undefined}
          >
            {t("chat/deepWork_6ufqdo")}
          </div>
          <MenuRadioGroup
            value={props.deepWorkEnabled ? "on" : "off"}
            onValueChange={(value) => {
              if (
                props.deepWorkDisabled ||
                !value ||
                value === (props.deepWorkEnabled ? "on" : "off")
              )
                return;
              props.onToggleDeepWork();
            }}
          >
            <MenuRadioItem value="on" disabled={props.deepWorkDisabled}>
              {t("chat/on_hltkdd")}
            </MenuRadioItem>
            <MenuRadioItem value="off" disabled={props.deepWorkDisabled}>
              {t("chat/off_hyg57y")}
            </MenuRadioItem>
          </MenuRadioGroup>
        </>
      ),
    });
  }
  if (props.activePlan) {
    sections.push({
      key: "plan-sidebar",
      node: (
        <MenuItem onClick={props.onTogglePlanSidebar}>
          <ListTodoIcon className="size-4 shrink-0" />
          {props.planSidebarOpen
            ? t("chat/hideSidebar_sdafdf", { planSidebarLabel })
            : t("chat/showSidebar_tx15eq", { planSidebarLabel })}
        </MenuItem>
      ),
    });
  }
  if (sections.length === 0) {
    return null;
  }
  return (
    <Menu>
      <MenuTrigger
        render={
          <Button
            size="sm"
            variant="ghost"
            className="shrink-0 px-2 text-muted-foreground/70 hover:text-foreground/80"
            aria-label={t("chat/moreComposerControls_6yi67s")}
          />
        }
      >
        <EllipsisIcon aria-hidden="true" className="size-4" />
      </MenuTrigger>
      <MenuPopup align="start">
        {sections.map((section, index) => (
          <Fragment key={section.key}>
            {index > 0 ? <MenuDivider /> : null}
            {section.node}
          </Fragment>
        ))}
      </MenuPopup>
    </Menu>
  );
});
